import React, { useState, useRef } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, ActivityIndicator, Alert, Image,
} from 'react-native';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { registerDeliveryPartner, updatePartnerProfile } from '../query/partnerService';
import { storage } from '../firebase/firebase';
import { useNavigate } from 'react-router-dom';

const STEPS = ['Account', 'Profile', 'Location', 'Review'];

interface FormErrors { [key: string]: string }

const PartnerRegistration: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Step 0 — Account
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [confirmPassword, setConfirm]   = useState('');
  const [showPass, setShowPass]         = useState(false);

  // Step 1 — Profile
  const [name, setName]         = useState('');
  const [phone, setPhone]       = useState('');
  const [dlNumber, setDlNumber] = useState('');
  const [pincode, setPincode]   = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUri, setImageUri]   = useState<string | null>(null);

  // Step 2 — Location
  const [latitude, setLatitude]   = useState('');
  const [longitude, setLongitude] = useState('');
  const [locLoading, setLocLoading] = useState(false);

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (step === 0) {
      if (!email.trim())                         e.email    = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(email))      e.email    = 'Invalid email format';
      if (!password)                             e.password = 'Password is required';
      else if (password.length < 6)              e.password = 'Minimum 6 characters';
      if (password !== confirmPassword)          e.confirm  = 'Passwords do not match';
    }
    if (step === 1) {
      if (!name.trim())  e.name  = 'Name is required';
      if (!phone.trim()) e.phone = 'Phone is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImageUri(URL.createObjectURL(file));
  };

  const getLocation = () => {
    setLocLoading(true);
    if (!navigator.geolocation) { setLocLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toString());
        setLongitude(pos.coords.longitude.toString());
        setLocLoading(false);
      },
      () => setLocLoading(false)
    );
  };

  const handleNext = () => {
    if (!validate()) return;
    setStep(s => s + 1);
  };

  const handleRegister = async () => {
    setLoading(true);
    try {
      // 1. Create Firebase Auth user + base Firestore doc
      const uid = await registerDeliveryPartner(email.trim(), password, {
        storename: name.trim(),
        phone: phone.trim(),
      });

      // 2. Upload profile image if selected
      let imageUrl: string | null = null;
      if (imageFile) {
        const imgRef = storageRef(storage, `partnerProfile/${uid}_${imageFile.name}`);
        await uploadBytes(imgRef, imageFile);
        imageUrl = await getDownloadURL(imgRef);
      }

      // 3. Update additional fields
      await updatePartnerProfile(uid, {
        PartnerDl: dlNumber,
        pincode,
        partnerLat: parseFloat(latitude) || 0,
        partnerLon: parseFloat(longitude) || 0,
        ...(imageUrl ? { image: imageUrl } : {}),
      });

      Alert.alert(
        'Registered!',
        'Delivery partner account created successfully.',
        [{ text: 'Done', onPress: () => navigate('/admin') }]
      );
    } catch (error: any) {
      Alert.alert('Registration Failed', error.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step indicator ───────────────────────────────────────────────
  const StepIndicator = () => (
    <View style={styles.indicator}>
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <View style={styles.stepDot}>
            <View style={[styles.dot, i <= step && styles.dotActive, i === step && styles.dotCurrent]}>
              {i < step
                ? <Text style={styles.dotCheckmark}>✓</Text>
                : <Text style={[styles.dotNum, i <= step && styles.dotNumActive]}>{i + 1}</Text>
              }
            </View>
            <Text style={[styles.stepLabel, i === step && styles.stepLabelActive]}>{label}</Text>
          </View>
          {i < STEPS.length - 1 && (
            <View style={[styles.connector, i < step && styles.connectorActive]} />
          )}
        </React.Fragment>
      ))}
    </View>
  );

  const ErrText = ({ field }: { field: string }) =>
    errors[field] ? <Text style={styles.errText}>{errors[field]}</Text> : null;

  const InputRow = ({ label, value, onChange, secure, keyType, placeholder }: any) => (
    <View style={styles.fieldGroup}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        style={[styles.input, errors[label.toLowerCase().replace(' *', '')] && styles.inputErr]}
        placeholder={placeholder || label}
        placeholderTextColor="#aaa"
        value={value}
        onChangeText={onChange}
        secureTextEntry={secure}
        keyboardType={keyType || 'default'}
        autoCapitalize="none"
      />
    </View>
  );

  return (
    <View style={styles.bg}>
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigate('/admin')} style={styles.backBtn}>
            <Text style={styles.backBtnText}>← Back</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Partner Registration</Text>
          <View style={{ width: 60 }} />
        </View>

        <StepIndicator />

        <View style={styles.card}>
          {/* ── Step 0: Account ── */}
          {step === 0 && (
            <>
              <Text style={styles.cardTitle}>Account Setup</Text>
              <Text style={styles.cardSubtitle}>Create login credentials for the partner</Text>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Email *</Text>
                <TextInput
                  style={[styles.input, errors.email && styles.inputErr]}
                  placeholder="partner@email.com"
                  placeholderTextColor="#aaa"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
                <ErrText field="email" />
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Password *</Text>
                <View style={styles.passRow}>
                  <TextInput
                    style={[styles.inputFlex, errors.password && styles.inputErr]}
                    placeholder="Min. 6 characters"
                    placeholderTextColor="#aaa"
                    value={password}
                    onChangeText={setPassword}
                    secureTextEntry={!showPass}
                  />
                  <TouchableOpacity onPress={() => setShowPass(s => !s)} style={styles.eyeBtn}>
                    <Text>{showPass ? '🙈' : '👁️'}</Text>
                  </TouchableOpacity>
                </View>
                <ErrText field="password" />
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Confirm Password *</Text>
                <TextInput
                  style={[styles.input, errors.confirm && styles.inputErr]}
                  placeholder="Re-enter password"
                  placeholderTextColor="#aaa"
                  value={confirmPassword}
                  onChangeText={setConfirm}
                  secureTextEntry={!showPass}
                />
                <ErrText field="confirm" />
              </View>
            </>
          )}

          {/* ── Step 1: Profile ── */}
          {step === 1 && (
            <>
              <Text style={styles.cardTitle}>Partner Profile</Text>
              <Text style={styles.cardSubtitle}>Basic information and photo</Text>

              {/* Profile image */}
              <View style={styles.imgSection}>
                <View style={styles.imgPicker}>
                  {imageUri
                    ? <Image source={{ uri: imageUri }} style={styles.imgPreview} />
                    : (
                      <View style={styles.imgPlaceholder}>
                        <Text style={styles.imgPlaceholderIcon}>📷</Text>
                        <Text style={styles.imgPlaceholderTxt}>Upload Photo</Text>
                      </View>
                    )
                  }
                  <input
                    ref={fileInputRef as any}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange as any}
                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                  />
                </View>
                {imageUri && (
                  <TouchableOpacity
                    onPress={() => { setImageUri(null); setImageFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    style={styles.removeImg}
                  >
                    <Text style={styles.removeImgText}>✕ Remove</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Full Name *</Text>
                <TextInput
                  style={[styles.input, errors.name && styles.inputErr]}
                  placeholder="Partner's name"
                  placeholderTextColor="#aaa"
                  value={name}
                  onChangeText={setName}
                />
                <ErrText field="name" />
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Phone Number *</Text>
                <TextInput
                  style={[styles.input, errors.phone && styles.inputErr]}
                  placeholder="+91 XXXXX XXXXX"
                  placeholderTextColor="#aaa"
                  value={phone}
                  onChangeText={setPhone}
                  keyboardType="phone-pad"
                />
                <ErrText field="phone" />
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Driving Licence Number</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. AS01-20230001234"
                  placeholderTextColor="#aaa"
                  value={dlNumber}
                  onChangeText={setDlNumber}
                  autoCapitalize="characters"
                />
              </View>

              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Pincode</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Area pincode"
                  placeholderTextColor="#aaa"
                  value={pincode}
                  onChangeText={setPincode}
                  keyboardType="number-pad"
                />
              </View>
            </>
          )}

          {/* ── Step 2: Location ── */}
          {step === 2 && (
            <>
              <Text style={styles.cardTitle}>Location</Text>
              <Text style={styles.cardSubtitle}>Set starting location for delivery</Text>

              <View style={styles.locBox}>
                <Text style={styles.locIcon}>📍</Text>
                <Text style={styles.locDesc}>
                  Tap the button below to auto-detect location, or enter coordinates manually.
                </Text>
                <TouchableOpacity style={styles.locBtn} onPress={getLocation} disabled={locLoading}>
                  {locLoading
                    ? <ActivityIndicator color="#fff" />
                    : <Text style={styles.locBtnText}>Detect My Location</Text>
                  }
                </TouchableOpacity>
              </View>

              <View style={styles.row}>
                <View style={{ flex: 1 }}>
                  <View style={styles.fieldGroup}>
                    <Text style={styles.label}>Latitude</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="26.030..."
                      placeholderTextColor="#aaa"
                      value={latitude}
                      onChangeText={setLatitude}
                      keyboardType="decimal-pad"
                    />
                  </View>
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.fieldGroup}>
                    <Text style={styles.label}>Longitude</Text>
                    <TextInput
                      style={styles.input}
                      placeholder="90.305..."
                      placeholderTextColor="#aaa"
                      value={longitude}
                      onChangeText={setLongitude}
                      keyboardType="decimal-pad"
                    />
                  </View>
                </View>
              </View>
            </>
          )}

          {/* ── Step 3: Review ── */}
          {step === 3 && (
            <>
              <Text style={styles.cardTitle}>Review & Register</Text>
              <Text style={styles.cardSubtitle}>Confirm all details before submitting</Text>

              {imageUri && (
                <View style={styles.reviewImgRow}>
                  <Image source={{ uri: imageUri }} style={styles.reviewImg} />
                  <Text style={styles.reviewImgLabel}>Profile Photo ✓</Text>
                </View>
              )}

              {([
                { section: 'Account',  items: [['Email', email]] },
                { section: 'Profile',  items: [['Name', name], ['Phone', phone], ['DL No.', dlNumber], ['Pincode', pincode]] },
                { section: 'Location', items: [['Latitude', latitude || '—'], ['Longitude', longitude || '—']] },
              ] as const).map(({ section, items }) => (
                <View key={section} style={styles.reviewSection}>
                  <Text style={styles.reviewSectionTitle}>{section}</Text>
                  {(items as unknown as [string, string][]).map(([k, v]) => v ? (
                    <View key={k} style={styles.reviewRow}>
                      <Text style={styles.reviewKey}>{k}</Text>
                      <Text style={styles.reviewVal} numberOfLines={1}>{v}</Text>
                    </View>
                  ) : null)}
                </View>
              ))}

              <View style={styles.termsBox}>
                <Text style={styles.termsText}>
                  By registering, you agree to Zapee's Terms of Service and Privacy Policy.
                </Text>
              </View>
            </>
          )}

          {/* Navigation buttons */}
          <View style={styles.navRow}>
            <TouchableOpacity
              style={styles.prevBtn}
              onPress={step === 0 ? () => navigate('/admin') : () => setStep(s => s - 1)}
            >
              <Text style={styles.prevBtnText}>{step === 0 ? 'Cancel' : '← Back'}</Text>
            </TouchableOpacity>

            {step < STEPS.length - 1 ? (
              <TouchableOpacity style={styles.nextBtn} onPress={handleNext}>
                <Text style={styles.nextBtnText}>Next →</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={styles.registerBtn} onPress={handleRegister} disabled={loading}>
                {loading
                  ? <ActivityIndicator color="#fff" />
                  : <Text style={styles.nextBtnText}>Register Partner</Text>
                }
              </TouchableOpacity>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  bg: { flex: 1, backgroundColor: '#f6f8fb' },
  scroll: { padding: 20, paddingBottom: 48 },

  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 24,
  },
  backBtn: { padding: 4 },
  backBtnText: { fontSize: 15, color: '#21808D', fontWeight: '600' },
  title: { fontSize: 20, fontWeight: '800', color: '#1a2332' },

  // Step indicator
  indicator: {
    flexDirection: 'row', alignItems: 'flex-start',
    justifyContent: 'center', marginBottom: 28,
  },
  stepDot: { alignItems: 'center', width: 60 },
  dot: {
    width: 34, height: 34, borderRadius: 17,
    backgroundColor: '#e2e8f0', alignItems: 'center', justifyContent: 'center',
    marginBottom: 6,
  },
  dotActive: { backgroundColor: '#21808D' },
  dotCurrent: { backgroundColor: '#21808D', shadowColor: '#21808D', shadowOpacity: 0.4, shadowRadius: 8, elevation: 4 },
  dotCheckmark: { color: '#fff', fontSize: 16, fontWeight: '700' },
  dotNum: { color: '#94a3b8', fontWeight: '700', fontSize: 14 },
  dotNumActive: { color: '#fff' },
  stepLabel: { fontSize: 10, color: '#94a3b8', textAlign: 'center', fontWeight: '500' },
  stepLabelActive: { color: '#21808D', fontWeight: '700' },
  connector: { flex: 1, height: 2, backgroundColor: '#e2e8f0', marginTop: 16 },
  connectorActive: { backgroundColor: '#21808D' },

  // Card
  card: {
    backgroundColor: '#fff', borderRadius: 16, padding: 24,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07, shadowRadius: 8, elevation: 3,
  },
  cardTitle: { fontSize: 20, fontWeight: '700', color: '#1a2332', marginBottom: 4 },
  cardSubtitle: { fontSize: 13, color: '#64748b', marginBottom: 24 },

  // Form
  fieldGroup: { marginBottom: 16 },
  label: { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: {
    height: 48, borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10,
    paddingHorizontal: 14, fontSize: 14, color: '#1a2332', backgroundColor: '#fafafa',
  },
  inputErr: { borderColor: '#ef4444' },
  errText: { fontSize: 12, color: '#ef4444', marginTop: 4 },
  row: { flexDirection: 'row', gap: 12 },

  // Password
  passRow: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, backgroundColor: '#fafafa' },
  inputFlex: { flex: 1, height: 48, paddingHorizontal: 14, fontSize: 14, color: '#1a2332' },
  eyeBtn: { paddingHorizontal: 14 },

  // Profile image
  imgSection: { alignItems: 'center', marginBottom: 24 },
  imgPicker: {
    width: 110, height: 110, borderRadius: 55,
    overflow: 'hidden', borderWidth: 2, borderColor: '#e2e8f0',
    backgroundColor: '#f6f8fb', position: 'relative',
    alignItems: 'center', justifyContent: 'center',
  },
  imgPreview: { width: 110, height: 110, borderRadius: 55 },
  imgPlaceholder: { alignItems: 'center' },
  imgPlaceholderIcon: { fontSize: 28 },
  imgPlaceholderTxt: { fontSize: 11, color: '#94a3b8', marginTop: 4 },
  removeImg: { marginTop: 8 },
  removeImgText: { fontSize: 13, color: '#ef4444', fontWeight: '600' },

  // Location
  locBox: { alignItems: 'center', backgroundColor: '#f0f9fa', borderRadius: 12, padding: 24, marginBottom: 20 },
  locIcon: { fontSize: 48, marginBottom: 12 },
  locDesc: { fontSize: 14, color: '#475569', textAlign: 'center', lineHeight: 20, marginBottom: 18 },
  locBtn: { backgroundColor: '#21808D', paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 },
  locBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },

  // Review
  reviewImgRow: { flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 16 },
  reviewImg: { width: 56, height: 56, borderRadius: 28, borderWidth: 2, borderColor: '#21808D' },
  reviewImgLabel: { fontSize: 13, color: '#10b981', fontWeight: '600' },
  reviewSection: { backgroundColor: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 12 },
  reviewSectionTitle: { fontSize: 13, fontWeight: '700', color: '#21808D', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  reviewRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  reviewKey: { fontSize: 13, color: '#64748b' },
  reviewVal: { fontSize: 13, fontWeight: '600', color: '#1a2332', flex: 1, textAlign: 'right' },
  termsBox: { backgroundColor: '#fff3e0', borderRadius: 8, padding: 12, marginTop: 8 },
  termsText: { fontSize: 12, color: '#92400e', textAlign: 'center' },

  // Nav buttons
  navRow: { flexDirection: 'row', gap: 12, marginTop: 28 },
  prevBtn: { flex: 1, height: 50, backgroundColor: '#f1f5f9', borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  prevBtnText: { fontSize: 15, fontWeight: '600', color: '#475569' },
  nextBtn: { flex: 2, height: 50, backgroundColor: '#21808D', borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  registerBtn: { flex: 2, height: 50, backgroundColor: '#10b981', borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  nextBtnText: { fontSize: 15, fontWeight: '700', color: '#fff' },
});

export default PartnerRegistration;
